import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {Items} from '@api/model/items'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {SyncCommitHandler} from '@client/sync-commit-handler'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {Column} from '@extensions/column'
import {globals} from '@extensions/globals-base'
import {Task} from '@extensions/task'
import {ActionType} from './multi-actions-with-tasks-store'

export const getTasksWithPermissionsToCheck = (
  tasksId: DataObjectId[]
): Hub[] => {
  const box = Sync.getCurrentSandbox()
  const tasks: Hub[] = []
  for (const taskId of tasksId) {
    const task = box.getHub(taskId)
    if (!task) {
      continue
    }
    if (!PermissionsChecker.checkComplete(task)) {
      continue
    }
    if (PermissionsChecker.shouldFallbackToOldCheckComplete(task)) {
      if (!PermissionsChecker.oldCheckComplete(task)) {
        continue
      }
    }
    tasks.push(task)
  }
  return tasks
}

export const getTasksWithPermissionsToArchive = (
  tasksId: DataObjectId[]
): Hub[] => {
  const box = Sync.getCurrentSandbox()
  const tasks: Hub[] = []
  for (const taskId of tasksId) {
    const task = box.getHub(taskId)
    if (!task) {
      continue
    }
    if (!PermissionsChecker.checkArchive(task)) {
      continue
    }
    tasks.push(task)
  }
  return tasks
}

export const getTasksWithPermissionsToMove = (
  tasksId: DataObjectId[]
): Hub[] => {
  const box = Sync.getCurrentSandbox()
  const tasks: Hub[] = []
  for (const taskId of tasksId) {
    const task = box.getHub(taskId)
    if (!task) {
      continue
    }
    if (!globals.dragAllowed(task.id)) {
      continue
    }
    tasks.push(task)
  }
  return tasks
}

export const getTasksWithPermissionsToDelete = (
  tasksId: DataObjectId[]
): Hub[] => {
  const box = Sync.getCurrentSandbox()
  const tasks: Hub[] = []
  for (const taskId of tasksId) {
    const task = box.getHub(taskId)
    if (!task) {
      continue
    }
    if (!PermissionsChecker.checkDeletion(task)) {
      continue
    }
    if (!Items.delete(Items.fromHub(task))) {
      continue
    }
    tasks.push(task)
  }
  return tasks
}

export const taskIsCompleted = (task: Hub): boolean => {
  return Task.getGTDStage(task) === 'done'
}

const moveTasks = (chosen: Hub[], tasksIds: DataObjectId[]): void => {
  const box = Sync.getCurrentSandbox()
  if (chosen.length > 0) {
    const column = chosen[0]
    SyncCommitHandler.doInBatch(() => {
      for (const taskId of tasksIds) {
        const task = box.getHub(taskId)
        if (!task) {
          continue
        }
        Column.taskDropHandler(0, task, column, {
          ignoreBoardFilters: true,
          type: 'menu'
        })
      }
    })
  }
}

export const commitAction = (
  tasksToAction: Hub[],
  currentAction: ActionType,
  opts?: {
    locationId: DataObjectId | undefined
  }
): void => {
  SyncCommitHandler.doInBatch(() => {
    if (currentAction === 'move') {
      if (!opts?.locationId) {
        logger.log('no location for move provided')
        return
      }
      const box = Sync.getCurrentSandbox()
      const hub = box.getHub(opts.locationId)
      if (!hub) {
        logger.log('location not fond', opts.locationId)
        return
      }
      moveTasks(
        [hub],
        tasksToAction.map(t => t.id)
      )
      return
    }
    for (const task of tasksToAction) {
      switch (currentAction) {
        case 'check': {
          Task.insertInGTD(
            task,
            'done',
            'multi-actions-with-tasks -> commitAction -> check'
          )
          void Tracking.send(TrackingEvent.taskCompleteTaskViaMultiActionsMenu)
          break
        }
        case 'uncheck': {
          Task.insertInGTD(
            task,
            'new',
            'multi-actions-with-tasks -> commitAction -> uncheck'
          )
          // void Tracking.send(TrackingEvent.myTasksUncompleteFromBoard)
          break
        }
        case 'archive': {
          void Task.changeArchived(task, true)
          void Tracking.send(TrackingEvent.taskArchive)
          break
        }
        case 'unarchive': {
          void Task.changeArchived(task, false)
          void Tracking.send(TrackingEvent.taskArchive)
          break
        }
        case 'delete': {
          Column.removeTask(task)
          break
        }
      }
    }
  })
}

export const checkTasks = (
  tasks: DataObjectId[]
): {
  checkedTasks: DataObjectId[]
  uncheckedTasks: DataObjectId[]
  archivedTasks: DataObjectId[]
  unarchivedTasks: DataObjectId[]
} => {
  const checkedTasks: DataObjectId[] = []
  const uncheckedTasks: DataObjectId[] = []
  const archivedTasks: DataObjectId[] = []
  const unarchivedTasks: DataObjectId[] = []

  const box = Sync.getCurrentSandbox()

  for (const id of tasks) {
    const task = box.getHub(id)
    if (!task) {
      continue
    }
    taskIsCompleted(task) && checkedTasks.push(id)
    !taskIsCompleted(task) && uncheckedTasks.push(id)
    Task.isArchived(task) && archivedTasks.push(id)
    !Task.isArchived(task) && unarchivedTasks.push(id)
  }

  return {checkedTasks, uncheckedTasks, archivedTasks, unarchivedTasks}
}

export const getTextWithoutPermission = (currentAction: ActionType): string => {
  switch (currentAction) {
    case 'archive':
      return Lang.msg.multiActionsWithTasks.withoutPermissionsArchive
    case 'check':
      return Lang.msg.multiActionsWithTasks.withoutPermissionsComplete
    case 'delete':
      return Lang.msg.multiActionsWithTasks.withoutPermissionsDelete
    case 'move':
      return Lang.msg.multiActionsWithTasks.withoutPermissionsMove
    case 'unarchive':
      return Lang.msg.multiActionsWithTasks.withoutPermissionsRestore
    case 'uncheck':
      return Lang.msg.multiActionsWithTasks.withoutPermissionUncomplete
    default:
      return ''
  }
}
